import axios, { type AxiosInstance } from 'axios'
import { DEFAULT_CURATED_RECITER } from '@/data/curatedReciters'

/**
 * islamic.network publishes some reciters only at a fixed bitrate; wrong bitrate returns 403.
 * See live checks against https://cdn.islamic.network/quran/audio/{bitrate}/{edition}/{ayah}.mp3
 */
const CDN_AYAH_BITRATE_BY_EDITION: Record<string, 64 | 128 | 192> = {
  'ar.abdurrahmaansudais': 192,
  'ar.saoodshuraym': 64,
}

/** Abdul Rashid Ali Sufi (حفص): gapless surah files + ayah timings on MP3Quran.net — not on islamic.network CDN. */
export const ABDURASHIDSUFI_EDITION_ID = 'ar.abdurashidsufi'
export const MP3QURAN_ABDURASHIDSUFI_TIMING_READ_ID = 258
const MP3QURAN_ABDURASHIDSUFI_SURAH_BASE =
  'https://server16.mp3quran.net/soufi/Rewayat-Hafs-A-n-Assem'

export function getMp3quranAbdurashidsufiSurahUrl(surahNumber: number): string {
  const n = String(Math.min(114, Math.max(1, Math.floor(surahNumber)))).padStart(3, '0')
  return `${MP3QURAN_ABDURASHIDSUFI_SURAH_BASE}/${n}.mp3`
}

/**
 * Quran API Client for alquran.cloud
 * Page text may call Quran Foundation (`VITE_QF_CONTENT_API_BASE`) from the browser; for production traffic at scale,
 * consider a same-origin reverse proxy to reduce third-party dependency and CORS exposure.
 * Documentation: https://alquran.cloud/api
 * Base URL: https://api.alquran.cloud/v1
 */
class QuranAPIClient {
  private client: AxiosInstance
  private readonly baseURL = 'https://api.alquran.cloud/v1'
  private readonly qfContentApiBase = import.meta.env.VITE_QF_CONTENT_API_BASE || 'https://api.quran.com/api/v4'

  constructor() {
    this.client = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    })
  }

  /**
   * Get available text and audio editions
   * GET /edition
   */
  async getEditions() {
    const response = await this.client.get('/edition')
    return response.data
  }

  /**
   * Get a complete Quran edition
   * GET /quran/:edition
   */
  async getQuran(edition?: string) {
    const endpoint = edition ? `/quran/${edition}` : '/quran/quran-uthmani'
    const response = await this.client.get(endpoint)
    return response.data
  }

  /**
   * Get a Juz of the Quran
   * GET /juz/:juzNumber/:edition?offset=:offset&limit=:limit
   */
  async getJuz(juzNumber: number, edition?: string, options?: { offset?: number; limit?: number }) {
    const ed = edition || 'quran-uthmani'
    let endpoint = `/juz/${juzNumber}/${ed}`
    const params = new URLSearchParams()
    if (options?.offset !== undefined) params.append('offset', options.offset.toString())
    if (options?.limit !== undefined) params.append('limit', options.limit.toString())
    if (params.toString()) endpoint += `?${params.toString()}`
    const response = await this.client.get(endpoint)
    return response.data
  }

  /**
   * Get a Surah of the Quran
   * GET /surah/:surahNumber/:edition?offset=:offset&limit=:limit
   */
  async getSurah(surahNumber: number, edition?: string, options?: { offset?: number; limit?: number }) {
    const ed = edition || 'quran-uthmani'
    let endpoint = `/surah/${surahNumber}/${ed}`
    const params = new URLSearchParams()
    if (options?.offset !== undefined) params.append('offset', options.offset.toString())
    if (options?.limit !== undefined) params.append('limit', options.limit.toString())
    if (params.toString()) endpoint += `?${params.toString()}`
    const response = await this.client.get(endpoint)
    return response.data
  }

  /**
   * Get an Ayah (verse) of the Quran
   * GET /ayah/:surahNumber/:ayahNumber/:edition
   */
  async getAyah(surahNumber: number, ayahNumber: number, edition?: string) {
    const ed = edition || 'quran-uthmani'
    const response = await this.client.get(`/ayah/${surahNumber}:${ayahNumber}/${ed}`)
    return response.data
  }

  /**
   * Search the text of the Quran
   * GET /search/:keyword/:surah/:edition/:language
   * @param keyword - The word to search for
   * @param surah - Surah number (1-114) or 'all' to search entire Quran
   * @param edition - Edition identifier (optional)
   * @param language - 2-digit language code (optional)
   */
  async search(keyword: string, surah: number | 'all' = 'all', edition?: string, language?: string) {
    const encodedKeyword = encodeURIComponent(keyword)
    const ed = edition || ''
    const lang = language || ''
    let endpoint = `/search/${encodedKeyword}/${surah}`
    if (ed) endpoint += `/${ed}`
    if (lang) endpoint += `/${lang}`
    const response = await this.client.get(endpoint)
    return response.data
  }

  /**
   * Get a Manzil of the Quran (7 Manzils total)
   * GET /manzil/:manzilNumber/:edition?offset=:offset&limit=:limit
   */
  async getManzil(manzilNumber: number, edition?: string, options?: { offset?: number; limit?: number }) {
    const ed = edition || 'quran-uthmani'
    let endpoint = `/manzil/${manzilNumber}/${ed}`
    const params = new URLSearchParams()
    if (options?.offset !== undefined) params.append('offset', options.offset.toString())
    if (options?.limit !== undefined) params.append('limit', options.limit.toString())
    if (params.toString()) endpoint += `?${params.toString()}`
    const response = await this.client.get(endpoint)
    return response.data
  }

  /**
   * Get a Ruku of the Quran (556 Rukus total)
   * GET /ruku/:rukuNumber/:edition?offset=:offset&limit=:limit
   */
  async getRuku(rukuNumber: number, edition?: string, options?: { offset?: number; limit?: number }) {
    const ed = edition || 'quran-uthmani'
    let endpoint = `/ruku/${rukuNumber}/${ed}`
    const params = new URLSearchParams()
    if (options?.offset !== undefined) params.append('offset', options.offset.toString())
    if (options?.limit !== undefined) params.append('limit', options.limit.toString())
    if (params.toString()) endpoint += `?${params.toString()}`
    const response = await this.client.get(endpoint)
    return response.data
  }

  /**
   * Quran Foundation Content API — list audio recitation styles (chapter-level catalog).
   * @see https://api-docs.quran.foundation/ — Content APIs
   */
  async getQfRecitationsCatalog() {
    const response = await axios.get(`${this.qfContentApiBase}/resources/recitations`)
    return response.data
  }

  /**
   * Get a Page of the Quran (604 pages total)
   * GET /page/:pageNumber/:edition?offset=:offset&limit=:limit
   */
  async getPage(pageNumber: number, edition?: string, options?: { offset?: number; limit?: number }) {
    const ed = edition || 'quran-uthmani'

    // Use Quran Foundation Content API for primary Quran page rendering.
    if (ed === 'quran-uthmani' || ed === 'quran-indopak') {
      try {
        const mushaf = ed === 'quran-indopak' ? 3 : 1
        const wordField = ed === 'quran-indopak' ? 'text_indopak' : 'text_uthmani'
        const includeTajweed = ed === 'quran-uthmani'
        const verses: any[] = []
        let apiPage = 1
        for (let _guard = 0; _guard < 24; _guard += 1) {
          const response = await axios.get(`${this.qfContentApiBase}/verses/by_page/${pageNumber}`, {
            params: {
              fields: includeTajweed ? 'text_uthmani,text_uthmani_tajweed' : 'text_indopak',
              words: true,
              word_fields: `${wordField},transliteration`,
              mushaf,
              per_page: 50,
              page: apiPage,
            },
          })
          const batch = response.data?.verses || []
          verses.push(...batch)
          const next = response.data?.pagination?.next_page as number | null | undefined
          if (next == null || next <= apiPage || batch.length === 0) break
          apiPage = next
        }
        const ayahs = verses.map((verse: any) => {
          const [surahNumberRaw] = String(verse.verse_key || '').split(':')
          const surahNumber = Number(surahNumberRaw) || 1
          const transliteration = Array.isArray(verse.words)
            ? verse.words
                .map((word: any) => word?.transliteration?.text || '')
                .filter(Boolean)
                .join(' ')
            : ''
          const text =
            ed === 'quran-indopak'
              ? verse.text_indopak ||
                (Array.isArray(verse.words)
                  ? verse.words.map((word: any) => word?.text_indopak || '').filter(Boolean).join(' ')
                  : '') ||
                verse.text_uthmani ||
                ''
              : verse.text_uthmani || ''
          return {
            text,
            textTajweed: includeTajweed ? verse.text_uthmani_tajweed || '' : '',
            transliteration,
            numberInSurah: verse.verse_number,
            page: verse.page_number || pageNumber,
            juz: verse.juz_number,
            surah: { number: surahNumber },
            surahNumber,
            ayahId: verse.id,
            verseKey: verse.verse_key,
          }
        })

        const uniqueSurahs = Array.from(new Set(ayahs.map((ayah: any) => ayah.surah?.number).filter(Boolean)))
        const firstVerse = verses[0]

        return {
          data: {
            ayahs,
            page: pageNumber,
            juz: firstVerse?.juz_number,
            surahs: uniqueSurahs.map((number) => ({ number })),
          },
        }
      } catch {
        // Fallback to alquran.cloud below
      }
    }

    let endpoint = `/page/${pageNumber}/${ed}`
    const params = new URLSearchParams()
    if (options?.offset !== undefined) params.append('offset', options.offset.toString())
    if (options?.limit !== undefined) params.append('limit', options.limit.toString())
    if (params.toString()) endpoint += `?${params.toString()}`
    const response = await this.client.get(endpoint)
    return response.data
  }

  /**
   * Get a Hizb Quarter of the Quran (240 Hizb Quarters total)
   * GET /hizbQuarter/:hizbQuarterNumber/:edition?offset=:offset&limit=:limit
   */
  async getHizbQuarter(hizbQuarterNumber: number, edition?: string, options?: { offset?: number; limit?: number }) {
    const ed = edition || 'quran-uthmani'
    let endpoint = `/hizbQuarter/${hizbQuarterNumber}/${ed}`
    const params = new URLSearchParams()
    if (options?.offset !== undefined) params.append('offset', options.offset.toString())
    if (options?.limit !== undefined) params.append('limit', options.limit.toString())
    if (params.toString()) endpoint += `?${params.toString()}`
    const response = await this.client.get(endpoint)
    return response.data
  }

  /**
   * Get all verses requiring Sajda / Prostration
   * GET /sajda/:edition
   */
  async getSajda(edition?: string) {
    const ed = edition || 'quran-uthmani'
    const response = await this.client.get(`/sajda/${ed}`)
    return response.data
  }

  /**
   * Get meta data about Surahs, Pages, Hizbs and Juzs
   * GET /meta
   */
  async getMeta() {
    const response = await this.client.get('/meta')
    return response.data
  }

  /**
   * Get translations for a verse
   * GET /ayah/:surahNumber:ayahNumber/:edition
   */
  async getTranslation(surahNumber: number, ayahNumber: number, translationEdition: string = 'en.sahih') {
    const response = await this.client.get(`/ayah/${surahNumber}:${ayahNumber}/${translationEdition}`)
    return response.data
  }

  /**
   * Get audio for a verse
   * GET /ayah/:surahNumber:ayahNumber/:edition (where edition is an audio edition)
   */
  async getAudio(surahNumber: number, ayahNumber: number, audioEdition: string = 'ar.abdulbasitmurattal') {
    const response = await this.client.get(`/ayah/${surahNumber}:${ayahNumber}/${audioEdition}`)
    return response.data
  }

  /**
   * CDN Methods - Direct access to media files
   */

  /**
   * Per-ayah CDN: default to 128 kbps (or 64 if requested ≤64). Some editions override via {@link CDN_AYAH_BITRATE_BY_EDITION}.
   */
  normalizeCdnAyahBitrate(bitrate: number, edition?: string): 64 | 128 | 192 {
    if (edition && CDN_AYAH_BITRATE_BY_EDITION[edition]) {
      return CDN_AYAH_BITRATE_BY_EDITION[edition]
    }
    const b = Number(bitrate) || 128
    if (b <= 64) return 64
    return 128
  }

  /**
   * Surah-level CDN uses the same bitrate rules as per-ayah where applicable.
   */
  normalizeCdnSurahBitrate(bitrate: number, edition?: string): 64 | 128 | 192 {
    return this.normalizeCdnAyahBitrate(bitrate, edition)
  }

  /**
   * Get CDN audio URL by ayah number
   * @param ayahNumber - Global ayah index as used by islamic.network (matches Quran.com verse id)
   * @param edition - Audio edition (reciter), e.g., 'ar.mahermuaiqly'
   * @param bitrate - Requested quality; coerced to 128 or 64 for CDN compatibility
   */
  getCDNAudioByAyah(ayahNumber: number, edition: string = DEFAULT_CURATED_RECITER, bitrate: number = 128): string {
    if (edition === ABDURASHIDSUFI_EDITION_ID) {
      return ''
    }
    const br = this.normalizeCdnAyahBitrate(bitrate, edition)
    return `https://cdn.islamic.network/quran/audio/${br}/${edition}/${ayahNumber}.mp3`
  }

  /**
   * Get CDN audio URL by surah
   * @param surahNumber - Surah number (1-114)
   * @param edition - Audio edition (reciter), e.g., 'ar.mahermuaiqly'
   * @param bitrate - Requested quality; surah bundles are requested at 128 kbps for compatibility
   */
  getCDNAudioBySurah(surahNumber: number, edition: string = DEFAULT_CURATED_RECITER, bitrate: number = 128): string {
    if (edition === ABDURASHIDSUFI_EDITION_ID) {
      return getMp3quranAbdurashidsufiSurahUrl(surahNumber)
    }
    const br = this.normalizeCdnSurahBitrate(bitrate, edition)
    return `https://cdn.islamic.network/quran/audio-surah/${br}/${edition}/${surahNumber}.mp3`
  }

  /**
   * Get CDN image URL for an ayah
   * @param surahNumber - Surah number (1-114)
   * @param ayahNumber - Ayah number relative to surah
   * @param font - Font style: 'indopak', 'tajweed', or custom
   * @param highResolution - Use high resolution image
   */
  getCDNAyahImage(surahNumber: number, ayahNumber: number, font: string = 'tajweed', highResolution: boolean = false): string {
    const basePath = highResolution ? 'high-resolution' : 'images'
    // Note: CDN may use different paths for different fonts
    // If font-specific paths exist, they would be: `${basePath}/${font}/${surahNumber}_${ayahNumber}.png`
    // For now, using standard path - you may need to adjust based on actual CDN structure
    if (font === 'indopak' || font === 'tajweed') {
      return `https://cdn.islamic.network/quran/${basePath}/${font}/${surahNumber}_${ayahNumber}.png`
    }
    return `https://cdn.islamic.network/quran/${basePath}/tajweed/${surahNumber}_${ayahNumber}.png`
  }

  /**
   * Get available audio editions (reciters) info
   * Fetches from CDN info endpoint
   */
  async getAudioEditionsInfo() {
    try {
      // Try JSON first
      const response = await axios.get('https://cdn.islamic.network/quran/info/by-ayah/info.json')
      return response.data
    } catch {
      // Fallback to API endpoint
      const response = await this.client.get('/edition/format/audio')
      return response.data
    }
  }

  /**
   * Get available audio editions for surah
   */
  async getSurahAudioEditionsInfo() {
    try {
      const response = await axios.get('https://cdn.islamic.network/quran/info/by-surah/info.json')
      return response.data
    } catch {
      const response = await this.client.get('/edition/format/audio')
      return response.data
    }
  }

  /**
   * Quran.com API v4 — one chapter with Medina tajweed XML (`<tajweed class=…>`) and word transliteration.
   * Used to enrich alquran.cloud surah/juz payloads (which omit both).
   */
  async getQfVersesForChapter(
    surahNumber: number,
  ): Promise<Map<number, { textTajweed: string; transliteration: string }>> {
    const map = new Map<number, { textTajweed: string; transliteration: string }>()
    let apiPage = 1
    for (let guard = 0; guard < 40; guard += 1) {
      try {
        const response = await axios.get(`${this.qfContentApiBase}/verses/by_chapter/${surahNumber}`, {
          params: {
            fields: 'text_uthmani,text_uthmani_tajweed',
            words: true,
            word_fields: 'text_uthmani,transliteration',
            mushaf: 1,
            per_page: 50,
            page: apiPage,
          },
        })
        const verses = response.data?.verses || []
        for (const verse of verses) {
          const vn = Number(verse.verse_number)
          const transliteration = Array.isArray(verse.words)
            ? verse.words.map((w: any) => w?.transliteration?.text || '').filter(Boolean).join(' ')
            : ''
          map.set(vn, {
            textTajweed: String(verse.text_uthmani_tajweed || ''),
            transliteration,
          })
        }
        const next = response.data?.pagination?.next_page as number | null | undefined
        if (next == null || next <= apiPage || verses.length === 0) break
        apiPage = next
      } catch {
        break
      }
    }
    return map
  }

  /**
   * Merge QF tajweed + transliteration onto ayahs from alquran (surah/juz). Ayahs must carry `surah.number` (or surah_number) and `numberInSurah`.
   */
  async enrichAyahsWithQfData(ayahs: any[]): Promise<any[]> {
    if (!ayahs.length) return ayahs
    const surahs = [
      ...new Set(
        ayahs.map((a) => Number(a?.surah?.number || a?.surah_number || a?.surah || 0)).filter((n) => n > 0 && n <= 114),
      ),
    ]
    const qfBySurah = new Map<number, Map<number, { textTajweed: string; transliteration: string }>>()
    await Promise.all(
      surahs.map(async (sn) => {
        try {
          qfBySurah.set(sn, await this.getQfVersesForChapter(sn))
        } catch {
          qfBySurah.set(sn, new Map())
        }
      }),
    )
    return ayahs.map((a) => {
      const sn = Number(a?.surah?.number || a?.surah_number || a?.surah || 0)
      const vn = Number(a?.numberInSurah ?? a?.number ?? 1)
      const qf = qfBySurah.get(sn)?.get(vn)
      const prevTr = String(a?.transliteration ?? '').trim()
      return {
        ...a,
        textTajweed: qf?.textTajweed ?? a.textTajweed ?? '',
        transliteration: (qf?.transliteration || prevTr || '').trim(),
      }
    })
  }
}

export const quranApi = new QuranAPIClient()

